import i18n from '@/i18n'
import { getUserPermissions } from '@/services/user-permissions'
import { getUserData } from '@/services/user-data'

const getAdministration = (permissions, modules) => {
    const { hasValues, hasTarget, hasQuiz, hasEnps, hasKpi } = modules

    const defaultMenuAdministration = {
        name: i18n.t('administrator.administrator'),
        url: '/v2/administrator',
        icon: 'fa fa-cog',
        children: [],
    }

    const hierarchy = {
        name: i18n.t('administrator.hierarchy'),
        url: '/v2/administrator/hierarchy',
    }

    const occupationGroups = {
        name: i18n.t('administrator.occupationGroups'),
        url: '/v2/administrator/occupation-groups',
    }

    const users = {
        name: i18n.t('administrator.users'),
        url: '/v2/administrator/users',
    }

    const periods = {
        name: i18n.t('administrator.periods'),
        url: '/v2/administrator/periods',
    }

    const tags = {
        name: i18n.t('administrator.tags'),
        url: '/v2/administrator/tags',
    }

    const integrations = {
        name: i18n.t('administrator.integrations'),
        url: '/v2/administrator/integrations',
    }

    const committees = {
        name: i18n.t('administrator.committees'),
        url: '/v2/administrator/committees',
    }

    const customers = {
        name: i18n.t('administrator.customers'),
        url: '/v2/administrator/customers',
    }

    const nBox = {
        name: 'N-Box',
        url: '/v2/n-box/list',
    }

    const eNPS = {
        name: 'eNPS',
        url: '/v2/administrator/enps',
    }

    const quiz = {
        name: i18n.t('moodPulse.moodPulse'),
        url: '/v2/administrator/mood-pulse',
    }

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, hierarchy]

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, occupationGroups]

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, users]

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, periods]

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, tags]

    defaultMenuAdministration.children = [...defaultMenuAdministration.children, integrations]

    if (hasValues || hasTarget || hasKpi)
        defaultMenuAdministration.children = [...defaultMenuAdministration.children, committees]

    if (permissions.role.name === 'god' && getUserData().company_id === 1) {
        defaultMenuAdministration.children = [...defaultMenuAdministration.children, customers]
    }

    if (hasValues || hasTarget) defaultMenuAdministration.children = [...defaultMenuAdministration.children, nBox]

    if (hasEnps) defaultMenuAdministration.children = [...defaultMenuAdministration.children, eNPS]

    if (hasQuiz) defaultMenuAdministration.children = [...defaultMenuAdministration.children, quiz]

    return defaultMenuAdministration
}

const getTarget = (hasTarget, hasKpi, isAdmin, isTargetMaster, isLocalAdministrator, isGod) => {
    const defaultMenuTarget = {
        name: 'Target',
        url: '/v2/target',
        icon: 'fa fa-bar-chart-o',
        children: [],
    }

    const rangesSubMenu = {
        name: i18n.t('ranges.ranges'),
        url: '/v2/target/ranges',
    }

    const unitsOfMeasurementSubMenu = {
        name: i18n.t('unitsOfMeasurement.unitsOfMeasurement'),
        url: '/v2/target/units-of-measurement',
    }

    const targetsSubMenu = {
        name: i18n.t('targets.target'),
        url: '/v2/target/targets',
    }

    const indicatorsSubMenu = {
        name: i18n.t('indicators.indicators'),
        url: '/v2/target/indicators',
    }

    if (hasKpi) {
        defaultMenuTarget.children = [...defaultMenuTarget.children, indicatorsSubMenu]
    }

    if (hasTarget) {
        defaultMenuTarget.children = [...defaultMenuTarget.children, targetsSubMenu]
    }

    if (isAdmin || isTargetMaster || isGod || isLocalAdministrator) {
        defaultMenuTarget.children = [...defaultMenuTarget.children, unitsOfMeasurementSubMenu]
    }

    if (isAdmin || isTargetMaster || isLocalAdministrator || isGod) {
        defaultMenuTarget.children = [...defaultMenuTarget.children, rangesSubMenu]
    }

    return defaultMenuTarget
}

const getPeople = (hasOkr) => {
    const people = {
        name: 'People',
        url: '/v2/people/list',
        icon: 'fa fa-users',
        children: [],
    }
    const peopleV1 = {
        name: 'People',
        url: '/v2/people/performance',
    }

    const peopleV2 = {
        name: 'People',
        badge: { text: 'new', variant: 'primary' },
        url: `/v2/people`,
    }

    if (hasOkr) {
        people.children = [...people.children, peopleV1]
    }

    people.children = [...people.children, peopleV2]

    return people
}

const getStrategy = () => {
    const defaultMenuStrategy = {
        name: 'Strategy',
        url: '/v2/strategy/onepage',
        icon: 'fa fa-map',
        children: [
            {
                name: 'OnePage',
                url: '/v2/strategy/onepage',
            },
        ],
    }
    return defaultMenuStrategy
}

const getOkr = (isAdmin, isLocalAdministrator, isOkrLeader, isGod) => {
    const defaultMenuOkr = {
        name: 'OKR',
        url: '/v2/okr',
        icon: 'fa fa-bullseye',
        children: [
            {
                name: i18n.t('okr.myOkrs'),
                url: `/v2/profile/${getUserData().id}?activeTab=okr`,
                attributes: { exact: true },
            },
            {
                name: i18n.t('okr.allOkrs'),
                url: '/v2/okr/all-okrs',
            },
            {
                name: i18n.t('profile.tabs.myInitiatives'),
                url: `/v2/profile/${getUserData().id}?activeTab=myInitiatives`,
                attributes: { exact: true },
            },
        ],
    }

    return defaultMenuOkr
}

const getReports = (
    hasOkr,
    hasTarget,
    hasValues,
    hasQuiz,
    hasEnps,
    isAdmin,
    isValuesMaster,
    isTargetMaster,
    isLocalAdministrator,
    isOkrLeader,
    isGod,
    hasAdminAccess
) => {
    const defaultMenuReports = {
        name: i18n.t('reports.reports'),
        url: '/v2/reports',
        icon: 'fa fa-th-large',
        children: [],
    }

    const dashboardSubMenu = {
        name: 'Dashboard',
        url: '/v2/dashboard',
    }

    const objectivesReportsSubMenu = {
        name: i18n.t('reports.objectives'),
        url: '/v2/reports/objectives',
    }

    const insightsSubMenu = {
        name: 'Insights',
        url: '/v2/insights',
    }

    const tagsSubMenu = {
        name: 'Tags',
        url: '/v2/reports/tags',
    }

    const nBoxSubMenu = {
        name: 'N-Box',
        url: '/v2/n-box/report',
    }

    const reportsSubMenu = {
        name: i18n.t('reports.reports'),
        url: '/v2/reports',
    }

    const eNpsSubMenu = {
        name: 'eNPS',
        url: '/v2/reports/enps',
    }

    const moodPulseSubMenu = {
        name: i18n.t('moodPulse.moodPulse'),
        url: '/v2/administrator/mood-pulse/report',
    }

    defaultMenuReports.children = [...defaultMenuReports.children, dashboardSubMenu]

    if (hasOkr && (isAdmin || isGod || isOkrLeader || isLocalAdministrator)) {
        defaultMenuReports.children = [...defaultMenuReports.children, objectivesReportsSubMenu]
    }

    if ((hasOkr || hasTarget) && (isAdmin || isTargetMaster || isLocalAdministrator || isGod)) {
        defaultMenuReports.children = [...defaultMenuReports.children, tagsSubMenu]
    }

    if (hasOkr && (isAdmin || isLocalAdministrator || isOkrLeader || isGod)) {
        defaultMenuReports.children = [...defaultMenuReports.children, insightsSubMenu]
    }

    if ((hasTarget || hasValues) && (isAdmin || isLocalAdministrator || isGod || isValuesMaster || isTargetMaster)) {
        defaultMenuReports.children = [...defaultMenuReports.children, nBoxSubMenu]
    }

    if (hasValues && (isAdmin || isLocalAdministrator || isGod || isValuesMaster)) {
        defaultMenuReports.children = [...defaultMenuReports.children, reportsSubMenu]
    }

    if (hasEnps && (isAdmin || isLocalAdministrator || isGod || hasAdminAccess)) {
        defaultMenuReports.children = [...defaultMenuReports.children, eNpsSubMenu]
    }

    if (hasQuiz && (isAdmin || isLocalAdministrator || isGod || hasAdminAccess)) {
        defaultMenuReports.children = [...defaultMenuReports.children, moodPulseSubMenu]
    }

    return defaultMenuReports
}

const getTalk = (isAdmin, isLocalAdministrator, isValuesMaster) => {
    const defaultMenuTalk = {
        name: 'Talk',
        url: '/v2/talk',
        icon: 'fa fa-comments',
        children: [
            {
                name: i18n.t('talk.feedbacks'),
                url: '/v2/feedbacks',
            },
        ],
    }

    const templates = {
        name: i18n.t('talk.templates'),
        url: '/v2/talk/templates',
    }

    if (isAdmin || isLocalAdministrator || isValuesMaster) {
        defaultMenuTalk.children = [templates, ...defaultMenuTalk.children]
    }

    return defaultMenuTalk
}

const getValues = (notifications, isAdmin, isValuesMaster, currentUserId) => {
    const defaultMenuValues = {
        name: i18n.t('values.moduleName'),
        url: '/v2/values',
        icon: 'fa fa-line-chart',
        children: [],
    }

    const networksSubMenu = {
        name: i18n.t('values.networks'),
        url: '/v2/values/networks',
    }

    const valuesSubMenu = [
        {
            name: i18n.t('values.scales'),
            url: '/v2/values/scales',
        },
        {
            name: i18n.t('values.categories'),
            url: '/v2/values/categories',
        },
        {
            name: i18n.t('values.subjects'),
            url: '/v2/values/subjects',
        },
        {
            name: i18n.t('values.questionnaires'),
            url: '/v2/values/questionnaires',
        },
        {
            name: i18n.t('values.myNetwork'),
            url: `/v2/values/valuers/${currentUserId}`,
        },
    ]

    if (isAdmin || isValuesMaster) {
        defaultMenuValues.children = [...defaultMenuValues.children, networksSubMenu]
    }

    if (isAdmin || isValuesMaster) {
        defaultMenuValues.children = [...defaultMenuValues.children, ...valuesSubMenu]
    }

    const evaluationWithBadge = {
        name: i18n.t('values.evaluate'),
        url: '/v2/values/evaluations',
        badge: {
            variant: 'danger',
            text: notifications,
        },
    }

    const evaluationWithoutBadge = {
        name: i18n.t('values.evaluate'),
        url: '/v2/values/evaluations',
    }

    if (notifications > 0) {
        return {
            ...defaultMenuValues,
            children: [...defaultMenuValues.children, evaluationWithBadge],
        }
    }

    return {
        ...defaultMenuValues,
        children: [...defaultMenuValues.children, evaluationWithoutBadge],
    }
}

const getHelpCenter = () => {
    return {
        name: i18n.t('helpCenter'),
        url: '/v2/help-center',
        icon: 'fa fa-question-circle',
    }
}

const getItems = (currentUser, currentUserPermissions, currentUserId, evaluationNotifications = 0) => {
    const { isAdmin, isLocalAdministrator, permissions, isGod } = currentUserPermissions

    const {
        has_modules,
        has_admin_access: hasAdminAccess,
        is_values_master: isValuesMaster,
        is_target_master: isTargetMaster,
        is_okr_leader: isOkrLeader,
        strategy_viewer: strategyViewer,
    } = currentUser

    const hasStrategy = has_modules.includes('has_strategy')
    const hasKpi = has_modules.includes('has_kpi')
    const hasOkr = has_modules.includes('has_okr')
    const hasTalk = has_modules.includes('has_talk')
    const hasValues = has_modules.includes('has_value')
    const hasTarget = has_modules.includes('has_goal')
    const hasPdi = has_modules.includes('has_pdi')
    const hasEnps = has_modules.includes('has_enps')
    const hasQuiz = has_modules.includes('has_quiz')

    let menu = []

    let home = {
        name: 'Home',
        url: `/v2/profile/${currentUserId}`,
        icon: 'fa fa-home',
    }

    menu = [...menu, home, getPeople(hasOkr, hasKpi, hasTarget, hasTalk, hasValues, hasPdi)]

    if (hasStrategy && (isAdmin || isLocalAdministrator || isGod || strategyViewer)) {
        menu = [...menu, getStrategy()]
    }

    if (hasTarget || hasKpi) {
        menu = [...menu, getTarget(hasTarget, hasKpi, isAdmin, isTargetMaster, isLocalAdministrator, isGod)]
    }

    if (hasOkr) {
        menu = [...menu, getOkr(isAdmin, isLocalAdministrator, isOkrLeader, isGod)]
    }

    if (hasTalk) {
        menu = [...menu, getTalk(isAdmin, isLocalAdministrator, isValuesMaster)]
    }

    if (hasValues) {
        menu = [...menu, getValues(evaluationNotifications, isAdmin, isValuesMaster, currentUserId)]
    }

    const userPermissions = getUserPermissions()

    menu = [
        ...menu,
        getReports(
            hasOkr,
            hasTarget,
            hasValues,
            hasQuiz,
            hasEnps,
            isAdmin,
            isValuesMaster,
            isTargetMaster,
            isLocalAdministrator,
            isOkrLeader,
            isGod,
            hasAdminAccess
        ),
    ]

    if (isAdmin || isLocalAdministrator || hasAdminAccess || userPermissions.is_god) {
        menu = [...menu, getAdministration(permissions, { hasValues, hasTarget, hasQuiz, hasEnps })]
    }

    menu = [...menu, getHelpCenter()]

    return menu
}

export default getItems
